import React, { useState } from "react";
import RadialBarGraph, { IRadialChartData } from "@Components/shared/RadialBarChart";
import { Switch } from "antd";
import { COLOURS } from "@Constants";
import { renderToStaticMarkup } from "react-dom/server";

export interface IGroupedRadialBarChartData {
  group: string;
  groupValue?: number;
  groups: string[];
  index: number;
  item: string;
  value: number;
}

type ISuffix = string | ((x: number) => string);

interface IProps {
  data: IGroupedRadialBarChartData[];
  suffix?: ISuffix;
  groupSuffix?: ISuffix;
  valueLabel?: string;
}

interface IItemData {
  color: string;
  label?: string;
  name: string;
  value: number;
}
interface IItem extends Omit<IItemData, "label"> {
  data: IGroupedRadialBarChartData;
}

const GroupedRadialBarChart: React.FC<IProps> = ({ data, groupSuffix, suffix = "", valueLabel, ...otherProps }) => {
  const [isGrouped, setisGrouped] = useState(data.length > 5);

  const formattedData: IRadialChartData[] = data.map((datum) => ({
    name: isGrouped ? datum.group : datum.item,
    ...datum,
  }));

  const formatTooltipLabel = ({ groups, item }: IGroupedRadialBarChartData) => (
    <span>
      {groups.join(" - ")} - <strong>{item}</strong>
    </span>
  );

  const formatSuffix = (currentSuffix: ISuffix, value: number): string =>
    typeof currentSuffix === "function" ? currentSuffix(value) : currentSuffix;

  const customContent = (title: string, items: IItem[]) =>
    renderToStaticMarkup(
      <>
        {items.length > 1 ? (
          <div className="g2-tooltip-title" style={{ marginTop: 16 }}>
            {title}
          </div>
        ) : null}
        <ul className="" style={{ paddingLeft: 0 }}>
          {items?.map((item, index) => {
            const { color, data: datum } = item;
            const { group, item: name, groupValue, value } = datum;
            const isLastItem = index === items.length - 1;
            const formattedSuffix = formatSuffix(suffix, value);
            const formattedGroupSuffix = !!groupSuffix ? formatSuffix(groupSuffix, groupValue) : formattedSuffix;

            return (
              <>
                {items.length === 1 ? (
                  <div className="g2-tooltip-title" style={{ marginTop: 16 }}>
                    {formatTooltipLabel(datum)}
                  </div>
                ) : null}
                <li
                  key={`${name}-${index}`}
                  className="g2-tooltip-list-item"
                  data-index={index}
                  style={{ marginBottom: 4, display: "flex", alignItems: "center" }}
                >
                  <span className="g2-tooltip-marker" style={{ backgroundColor: color }}></span>
                  <span className="tooltip-label">
                    <span style={{ marginRight: 16 }}>{valueLabel ?? name}:</span>
                    <span className="g2-tooltip-list-item-value">{`${value} ${formattedSuffix}`}</span>
                  </span>
                </li>
                {isGrouped && isLastItem ? (
                  <li
                    key={`${name}-${index}`}
                    className="g2-tooltip-list-item"
                    data-index={index}
                    style={{ marginBottom: 4, display: "flex", alignItems: "center" }}
                  >
                    <span className="g2-tooltip-marker" style={{ backgroundColor: COLOURS.Grey }}></span>
                    <span className="tooltip-label">
                      <span style={{ marginRight: 16 }}>{`${group}${valueLabel ? ` ${valueLabel}` : ""}`}:</span>
                      <span className="g2-tooltip-list-item-value">{`${groupValue} ${formattedGroupSuffix}`}</span>
                    </span>
                  </li>
                ) : null}
              </>
            );
          })}
        </ul>
      </>
    );

  const handleChange = (value: boolean) => {
    setisGrouped(value);
    const customEvent = new CustomEvent("chart:is-grouped-toggle", {
      bubbles: true,
      cancelable: true,
      detail: { isGrouped: value },
    });
    document.dispatchEvent(customEvent);
  };

  return (
    <div>
      <Switch
        checked={isGrouped}
        className="position-top-right"
        checkedChildren={"Grouped"}
        onChange={handleChange}
        unCheckedChildren={"Ungrouped"}
      />
      <RadialBarGraph
        colorField={isGrouped ? "index" : "value"}
        data={formattedData}
        isStack={isGrouped}
        tooltip={{ customContent }}
        {...otherProps}
      />
    </div>
  );
};

export default GroupedRadialBarChart;
